'use client';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '#/components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from '#/components/ui/sidebar';
import { PermissionId } from '#/constants/PermissionId';
import { useTranslation } from '#/contexts/useTranslation';
import { useUserPermission } from '#/data/useUserInfo';
import { cn } from '#/lib/utils';
import { ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useSelectedLayoutSegments } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { AppSidebarFooter } from './app-sidebar-footer';
import { AppSidebarHeader } from './app-sidebar-header';
import { useAppSidebarStore } from './app-sidebar-store';
import { ReactComponent as ApiKeyManagerIcon } from './images/api-key-manager.svg';
import { ReactComponent as DashboardIcon } from './images/dashboard.svg';
import { ReactComponent as EthereumEthLogoIcon } from './images/ethereum-eth-logo.svg';

export function AppSidebar() {
  const translation = useTranslation();
  const hasApiKeyPermission = useUserPermission(PermissionId.API_KEY);

  const items = useMemo(
    () =>
      [
        {
          title: translation.EthereumStaking.Heading,
          description: translation.EthereumStaking.HeadingHelp,
          icon: <EthereumEthLogoIcon className="h-6 w-5" />,
          children: [
            {
              title: translation.my_staking.Heading.Title,
              url: '/ethereum/staking',
            },
            {
              title: translation.rewards.title,
              url: '/ethereum/rewards',
            },
            // {
            //   title: translation.ClaimAddresses.Heading.Title,
            //   url: '/ethereum/claim-addresses',
            // },
          ],
        },
        // {
        //   title: translation.EthereumStaking.Heading,
        //   description: translation.EthereumStaking.HeadingHelp,
        //   url: '/ethereum/staking',
        //   icon: <EthereumEthLogoIcon className="h-6 w-5" />,
        // },
        {
          title: translation.PortfolioDashboard.Heading,
          description: translation.PortfolioDashboard.HeadingHelp,
          url: '/dashboard',
          icon: <DashboardIcon className="h-5 w-5" />,
        },
        // {
        //   title: translation.RewardsReporting.Heading,
        //   description: translation.RewardsReporting.HeadingHelp,
        //   url: '/reporting',
        //   icon: <ReportingIcon className="h-5 w-5" />,
        // },
        {
          title: translation.APIKeyManager.Heading,
          url: '/apikey',
          icon: <ApiKeyManagerIcon className="h-5 w-5" />,
          disabled: !hasApiKeyPermission,
        },
      ].filter(v => !v.disabled),
    [translation, hasApiKeyPermission]
  );

  const segments = useSelectedLayoutSegments();

  const selectedKeys = useMemo(() => {
    return segments.map((segment, i) => {
      return `/${segments.slice(0, i + 1).join('/')}`;
    });
  }, [segments]);

  const isActive = useCallback(
    (url: string) => selectedKeys.includes(url),
    [selectedKeys]
  );

  const isChildActive = useCallback(
    (item: { children?: { url?: string }[] }) =>
      item.children?.some(child => isActive(child.url!)),
    [isActive]
  );

  const sidebar = useAppSidebarStore(state => state.sidebar);

  return (
    <Sidebar>
      <AppSidebarHeader />
      <SidebarContent className="md:pt-17">
        <SidebarGroup>
          <SidebarMenu>
            {items.map((item, i) => {
              return item != null && item.children != null ? (
                <Collapsible key={i} defaultOpen className="group/collapsible">
                  <SidebarMenuItem className="space-y-1">
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        className={cn('whitespace-nowrap h-9', {
                          'text-blue-600 hover:text-blue-600!':
                            isChildActive(item),
                        })}
                      >
                        {item.icon}
                        <span>{item.title}</span>
                        <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub className="border-none">
                        {item.children.map((item, i) => (
                          <SidebarMenuSubItem key={i}>
                            <SidebarMenuSubButton
                              asChild
                              className={cn('h-9', {
                                'text-blue-600 bg-blue-50 hover:text-blue-600 hover:bg-blue-50':
                                  isActive(item.url),
                              })}
                            >
                              <Link
                                href={item.url}
                                tabIndex={isActive(item.url) ? -1 : 0}
                              >
                                {item.title}
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              ) : (
                <SidebarMenuItem key={i}>
                  <SidebarMenuButton
                    asChild
                    className={cn('h-9', {
                      'text-blue-600 bg-blue-50 hover:text-blue-600! hover:bg-blue-50!':
                        isActive(item.url),
                    })}
                  >
                    <Link href={item.url}>
                      {item.icon}
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      {sidebar?.isMobile && <AppSidebarFooter />}
      <SidebarRail />
    </Sidebar>
  );
}
